import React, { useEffect, useMemo, useState } from "react";
import ACUpdateMarksSidePanel from "../../components/ACUpdateMarkSubmission/ACUpdateMarksSidePanel";
import ACUpdateMarksCentrePanel from "../../components/ACUpdateMarkSubmission/ACUpdateMarksCentrePanel";
import AssessmentCriteriaOriginalTable from "../../components/ACUpdateMarkSubmission/AssessmentCriteriaOriginalTable";
import AssessmentCriteriaUpdatedTable from "../../components/ACUpdateMarkSubmission/AssessmentCriteriaUpdatedTable";
import "./ACUpdateMarksDashboardPage.css";
import { useCenter } from "../../context/CenterContext";
import { useLocation, useNavigate } from "react-router-dom";
import { useApi } from "../../context/ApiClient";
import UpdateConfirmSubmitModal from "../../components/ACUpdateMarkSubmission/UpdateConfirmSubmitModal";
import { useSubject } from "../../context/SubjectContext";
import toast, { Toaster } from "react-hot-toast";
import {
  mapAssessmentNameToAchieved,
  mapAchievedToAssessmentName,
} from "../../utils/updateAssessmentCriteriaMappings";
import {
  AssessmentCriteriaInputValues,
  MARK_STATUS,
  AssessmentCriteria
} from "../../constants/Constants";
import UpdateACMarksConfirmModal from "../../components/UpdateMarkSubmission/UpdateACMarksConfirmModal";
import { getCenterType, checkunitIsConsortiaUnit } from "../../utils/utils";
import { useConsortiaCenter } from "../../context/ConsortiaCenterContext";

const ACUpdateMarksDashboardPage = () => {
  const location = useLocation();
  const { centerNumber } = useCenter();
  const { subjectData } = useSubject();
  const navigate = useNavigate();
  const subjectId = subjectData?.selectedSubjectId;
  const baseUrl = process.env.REACT_APP_AMS_BASE_URL;
  const { unit, sessionId, mode = "edit" } = location.state || {};

  const [showModal, setShowModal] = useState(false);
  const [showUpdateMarksModal, setShowUpdateMarksModal] = useState(false);
  const [updateMarksCandidate, setUpdateMarksCandidate] = useState([]);
  const [updatedTotalCriteriaScore, setUpdatedTotalCriteriaScore] = useState(
    {},
  );
  const [isSystemTransition, setIsSystemTransition] = useState(false);

  const isViewMode = mode === "view";
  useEffect(() => {
    if (isViewMode) {
      setShowOriginalMarksError(false);
      setShowUpdatedSection(true);
      setCurrentSection("updatedMarks");
      setIsUpdatedActiveUI(true);
    }
  }, [isViewMode]);

  const [candidates, setCandidates] = useState([]);
  const { selectedSubject, selectedSubjectId } = subjectData || {};
  const [disableOriginalMarks, setDisableOriginalMarks] = useState(false);
  const [selectedCandidateId, setSelectedCandidateId] = useState(null);
  const [savedSnapshot, setSavedSnapshot] = useState({});
  const [currentSection, setCurrentSection] = useState("originalMarks");
  const [showUpdatedSection, setShowUpdatedSection] = useState(false);
  const [showOriginalMarksError, setShowOriginalMarksError] = useState(false);
  const [isUpdatedActiveUI, setIsUpdatedActiveUI] = useState(false);
  const [totalMarks, setTotalMarks] = useState(0);
  const [originalTotalCriteriaScore, setOriginalTotalCriteriaScore] =
    useState(0);


  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [candidateAchievedCounts, setCandidateAchievedCounts] = useState({});
  const [criteriaAchievedCount, setCriteriaAchievedCount] = useState({});
  const [updatedCriteriaAchievedCount, setUpdatedCriteriaAchievedCount] =
    useState({});
  const [missingOrInvalidCandidates, setMissingOrInvalidCandidates] = useState(
    [],
  );

  const unitCode = useMemo(() => (unit ? unit.split(" ")[0] : ""), [unit]);

  const candidateMarks = 6; // number of criteria
  const consortiaUnitcode =
    String(unit ?? "")
      .toUpperCase()
      .match(/^[A-Z0-9]+/)?.[0] || "";
  const { consortiaCentres = [] } = useConsortiaCenter();

  const isConsortiaUnit = checkunitIsConsortiaUnit(
    consortiaCentres,
    centerNumber,
    sessionId,
    consortiaUnitcode,
  );
  const currentCentreType =
    getCenterType(consortiaCentres, Number(centerNumber), selectedSubject, selectedSubjectId) || "";

  const api = useApi((["Rest", "Lead"].includes(currentCentreType) && isConsortiaUnit) ? `${baseUrl}consortia/` : baseUrl);

  const openSubmitModal = () => {
    currentSection === "updatedMarks"
      ? setShowModal(true)
      : setIsSubmitModalOpen(true);
  };
  const closeSubmitModal = () => setIsSubmitModalOpen(false);

  const safeCandidates = useMemo(
    () => (Array.isArray(candidates) ? candidates.filter(Boolean) : []),
    [candidates],
  );

  const formatFromApi = (data) => {
    return (data || []).map((val, indx) => {
      const { criteriaDetails = [], ...common } = val;

      // Original criteria from BE
      const criteriaOriginal = criteriaDetails.map((c) => ({
        assessmentCriteriaId: c.assessmentCriteriaId,
        assessmentCriteriaName: c.assessmentCriteriaName,
        assessmentCriteriaDescription: c.assessmentCriteriaDescription,
        totalMarks: c.totalMarks,
        sortOrder: c.sortOrder,
        originalMarkStatus: c.originalMarkStatus,
        originalTotalCriteriaScore: c.originalTotalCriteriaScore,
        originalCentreAssessmentName: c.originalCentreAssessmentName ? c.originalCentreAssessmentName : AssessmentCriteria.NOT_ATTEMPTED,
        originalEvidenceLocation: c.originalEvidenceLocation,
        originalInsertedDate: c.originalInsertedDate,
      }));

      // Read local overrides using centreCandidateNumber (fallback to candidateNumber)
      const candidateIdForKey = String(
        common.centreCandidateNumber ?? common.candidateNumber
      );
      const localKey = `updatedSelections_${centerNumber}_${candidateIdForKey}_${subjectId}`;
      const localSelections = JSON.parse(localStorage.getItem(localKey)) || {};

      // Updated criteria: apply rules combining BE, isAgreed, and local overrides
      const criteriaUpdated = criteriaDetails.map((c) => {
        const overrideRaw = localSelections[c.assessmentCriteriaName]; // "yes"/"no"/"not_attempted"
        const overrideAssessmentName = overrideRaw
          ? mapAchievedToAssessmentName(overrideRaw)
          : undefined;
        let updatedCentreAssessmentName;
        let updatedEvidenceLocation;

        if (!overrideAssessmentName) {
          // Local storage empty
          if (c.isAgreed === false && !isViewMode) {
            // isAgreed false + no local override => clear (in edit mode)
            updatedCentreAssessmentName = null;
            updatedEvidenceLocation = "";
          } else {
            // isAgreed true OR view mode => use BE values
            updatedCentreAssessmentName = c.updatedCentreAssessmentName;
            updatedEvidenceLocation = c.updatedEvidenceLocation;
          }
        } else {
          // Local storage has override
          if (c.isAgreed === false) {
            // isAgreed false + local override => use local override
            updatedCentreAssessmentName = overrideAssessmentName;
            // Keep BE notes unless you also store notes in local storage
            updatedEvidenceLocation = c.updatedEvidenceLocation;
          } else {
            // isAgreed true + local override => use BE values
            updatedCentreAssessmentName = c.updatedCentreAssessmentName;
            updatedEvidenceLocation = c.updatedEvidenceLocation;
          }
        }

        return {
          assessmentCriteriaId: c.assessmentCriteriaId,
          assessmentCriteriaName: c.assessmentCriteriaName,
          assessmentCriteriaDescription: c.assessmentCriteriaDescription,
          totalMarks: c.totalMarks,
          sortOrder: c.sortOrder,
          updatedMarkStatus: c.updatedMarkStatus,
          updatedTotalCriteriaScore: c.updatedTotalCriteriaScore,
          updatedCentreAssessmentName,
          updatedEvidenceLocation,
          updatedInsertedDate: c.updatedInsertedDate,
          isAgreed: c.isAgreed,
          originalCentreAssessmentName: c.originalCentreAssessmentName,
          originalEvidenceLocation: c.originalEvidenceLocation,
        };
      });
      return {
        ...common,
        candidateNumber:
          common.candidateNumber || common.centreCandidateNumber,
        centreCandidateNumber:
          common.centreCandidateNumber || common.candidateNumber,
        criteriaOriginal,
        criteriaUpdated,
      };
    });
  };

  const fetchCandidates = async () => {
    try {
      let endpoint = ''
      if (isConsortiaUnit === undefined || isConsortiaUnit === null) { return; }
      if (['Lead', 'Rest'].includes(currentCentreType) && isConsortiaUnit) {
        endpoint = 'getConsortiaStage3CandidateAssessmentCriteria'
      } else {
        endpoint = "getStage3CandidateAssessmentCriteria"
      }

      const response = await api.get(endpoint, {
        params: {
          assessmentCode: unitCode,
          centreNumber: centerNumber,
          sessionId: sessionId,
        },
      });

      const formatted = formatFromApi(response.data?.data || []).sort((a, b) =>
        (a.candidateName || "").localeCompare(b.candidateName || ""),
      );

      const cleaned = Array.isArray(formatted) ? formatted.filter(Boolean) : [];

      const originals = cleaned?.[0]?.criteriaOriginal || [];
      setDisableOriginalMarks(
        originals.some((val) => val.originalMarkStatus === "Re-submitted"),
      );
      originals.some((val) => val.originalMarkStatus === "Re-submitted") ? setIsUpdatedActiveUI(true) : setIsUpdatedActiveUI(false)

      setCandidates(cleaned);
      // Compute updated total criteria score for each candidate
      const totals = {};
      cleaned.forEach((candidate) => {
        const candidateId = candidate.centreCandidateNumber;
        const firstCriteria = candidate.criteriaUpdated?.[0];
        totals[candidateId] = firstCriteria?.updatedTotalCriteriaScore || 0;
      });
      setUpdatedTotalCriteriaScore(totals)

      if (cleaned.length > 0 && !selectedCandidateId) {
        const firstId = cleaned[0].centreCandidateNumber;

        setSelectedCandidateId(firstId);

        setSavedSnapshot({
          [firstId]: {
            criteriaOriginal: JSON.parse(
              JSON.stringify(cleaned[0].criteriaOriginal || []),
            ),
            criteriaUpdated: JSON.parse(
              JSON.stringify(cleaned[0].criteriaUpdated || []),
            ),
          },
        });
      }

      return cleaned;
    } catch (err) {
      console.error("Error fetching data:", err);
      return [];
    }
  };

  useEffect(() => {
    const stored = localStorage.getItem("acUpdateMarksData");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        const cleanedCandidates = Array.isArray(parsed.candidates)
          ? parsed.candidates.filter(Boolean)
          : [];
        setCandidates(cleanedCandidates);
        setSavedSnapshot(parsed.savedSnapshot || {});
        setSelectedCandidateId(parsed.selectedCandidateId || null);
        setShowUpdatedSection(parsed.showUpdatedSection || false);
        setCurrentSection(parsed.currentSection || "originalMarks");
      } catch (e) {
        console.error("Failed to parse localStorage data", e);
        localStorage.removeItem("acUpdateMarksData");
      }
    }
    fetchCandidates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedCandidate = useMemo(
    () =>
      safeCandidates.find(
        (c) =>
          String(c?.centreCandidateNumber) === String(selectedCandidateId) ||
          String(c.candidateNumber) === String(selectedCandidateId),
      ),
    [safeCandidates, selectedCandidateId],
  );

  useEffect(() => {
    if (!selectedCandidate) return;
    const firstOriginal = selectedCandidate.criteriaOriginal?.[0];
    if (!firstOriginal) return;
    setTotalMarks(firstOriginal.totalMarks || 0);
    setOriginalTotalCriteriaScore(
      firstOriginal.originalTotalCriteriaScore || 0,
    );
  }, [selectedCandidate]);

  const updateOriginalCriteriaField = (
    candidateId,
    criteriaName,
    field,
    value,
  ) => {
    setCandidates((prev) =>
      (Array.isArray(prev) ? prev : []).filter(Boolean).map((cand) => {
        if (String(cand.centreCandidateNumber) !== String(candidateId))
          return cand;
        return {
          ...cand,
          criteriaOriginal: (cand.criteriaOriginal || []).map((crit) =>
            crit.assessmentCriteriaName === criteriaName
              ? { ...crit, [field]: value }
              : crit,
          ),
        };
      }),
    );
  };

  const onChangeValue = (candidateId, criteriaName, value) => {
    const assessmentName = mapAchievedToAssessmentName(value);
    updateOriginalCriteriaField(
      candidateId,
      criteriaName,
      "originalCentreAssessmentName",
      assessmentName,
    );

    const candidate = safeCandidates.find(
      (c) => String(c?.centreCandidateNumber) === String(candidateId),
    );
    if (candidate) {
      const updatedCriteria = (candidate.criteriaOriginal || []).map((crit) =>
        crit.assessmentCriteriaName === criteriaName
          ? { ...crit, originalCentreAssessmentName: assessmentName }
          : crit,
      );

      const yesCount = updatedCriteria.filter((crit) => {
        const achieved = mapAssessmentNameToAchieved(
          crit.originalCentreAssessmentName,
        );
        return achieved === "yes";
      }).length;

      setCriteriaAchievedCount((prev) => ({
        ...prev,
        [candidateId]: yesCount,
      }));

      const candidateMarksVal =
        updatedCriteria[0]?.originalTotalCriteriaScore || 0;

      setCandidateAchievedCounts((prev) => ({
        ...prev,
        [candidateId]: {
          achievedCount: yesCount,
          candidateMarks: candidateMarksVal,
        },
      }));
    }
  };

  const onChangeNote = (candidateId, criteriaName, value) => {
    updateOriginalCriteriaField(
      candidateId,
      criteriaName,
      "originalEvidenceLocation",
      value,
    );
  };

  const updateUpdatedCriteriaField = (
    candidateId,
    criteriaName,
    field,
    value,
  ) => {
    setCandidates((prev) =>
      (Array.isArray(prev) ? prev : []).filter(Boolean).map((cand) => {
        if (String(cand.centreCandidateNumber) !== String(candidateId))
          return cand;
        return {
          ...cand,
          criteriaUpdated: (cand.criteriaUpdated || []).map((crit) =>
            crit.assessmentCriteriaName === criteriaName
              ? { ...crit, [field]: value }
              : crit,
          ),
        };
      }),
    );
  };

  const onChangeUpdatedValue = (candidateId, criteriaName, value) => {
    const assessmentName = mapAchievedToAssessmentName(value);
    updateUpdatedCriteriaField(
      candidateId,
      criteriaName,
      "updatedCentreAssessmentName",
      assessmentName,
    );

    const candidate = safeCandidates.find(
      (c) => String(c?.centreCandidateNumber) === String(candidateId),
    );
    if (candidate) {
      const updatedCriteria = (candidate.criteriaUpdated || []).map((crit) =>
        crit.assessmentCriteriaName === criteriaName
          ? { ...crit, updatedCentreAssessmentName: assessmentName }
          : crit,
      );

      const yesCount = updatedCriteria.filter((crit) => {
        const achieved = mapAssessmentNameToAchieved(
          crit.updatedCentreAssessmentName,
        );
        return achieved === "yes";
      }).length;
      // Only update updated counts here; keep original counts stable
      setUpdatedCriteriaAchievedCount((prev) => ({
        ...prev,
        [candidateId]: yesCount,
      }));
    }
  };

  const onChangeUpdatedNote = (candidateId, criteriaName, value) => {
    updateUpdatedCriteriaField(
      candidateId,
      criteriaName,
      "updatedEvidenceLocation",
      value,
    );
  };

  const originalValues = useMemo(() => {
    if (!selectedCandidate) return {};
    const map = {};
    (selectedCandidate.criteriaOriginal || []).forEach((crit) => {
      map[crit.assessmentCriteriaName] = mapAssessmentNameToAchieved(
        crit.originalCentreAssessmentName,
      );
    });
    return map;
  }, [selectedCandidate]);

  const originalNotes = useMemo(() => {
    if (!selectedCandidate) return {};
    const candidateId = selectedCandidate.centreCandidateNumber;
    const inner = {};
    (selectedCandidate.criteriaOriginal || []).forEach((crit) => {
      inner[crit.assessmentCriteriaName] = crit.originalEvidenceLocation || "";
    });
    return { [candidateId]: inner };
  }, [selectedCandidate]);

  const updatedValues = useMemo(() => {
    if (!selectedCandidate) return {};
    const map = {};
    (selectedCandidate.criteriaUpdated || []).forEach((crit) => {
      map[crit.assessmentCriteriaName] = mapAssessmentNameToAchieved(
        crit.updatedCentreAssessmentName,
      );
    });
    return map;
  }, [selectedCandidate]);

  const updatedNotes = useMemo(() => {
    if (!selectedCandidate) return {};
    const candidateId = selectedCandidate.centreCandidateNumber;
    const inner = {};
    (selectedCandidate.criteriaUpdated || []).forEach((crit) => {
      inner[crit.assessmentCriteriaName] = crit.updatedEvidenceLocation || "";
    });
    return { [candidateId]: inner };
  }, [selectedCandidate]);

  useEffect(() => {
    const safe = Array.isArray(candidates) ? candidates.filter(Boolean) : [];
    if (!safe.length) return;

    const originalCounts = {};
    const originalYesCounts = {};
    const updatedYesCounts = {};

    safe.forEach((cand) => {
      const cid = cand.centreCandidateNumber;

      const yesCountOriginal = (cand.criteriaOriginal || []).filter((crit) => {
        const achieved = mapAssessmentNameToAchieved(
          crit.originalCentreAssessmentName,
        );
        return achieved === "yes";
      }).length;

      const totalOriginal =
        cand.criteriaOriginal?.[0]?.originalTotalCriteriaScore || 0;

      originalCounts[cid] = {
        achievedCount: yesCountOriginal,
        candidateMarks: totalOriginal,
      };
      originalYesCounts[cid] = yesCountOriginal;

      const yesCountUpdated = (cand.criteriaUpdated || []).filter((crit) => {
        const achieved = mapAssessmentNameToAchieved(
          crit.updatedCentreAssessmentName,
        );
        return achieved === "yes";
      }).length;
      updatedYesCounts[cid] = yesCountUpdated;
    });

    setCandidateAchievedCounts(originalCounts);
    setCriteriaAchievedCount(originalYesCounts);
    setUpdatedCriteriaAchievedCount(updatedYesCounts);
  }, [candidates]);

  const hasUnsavedChangesFor = (candidateId, type) => {
    if (isSystemTransition) return false;

    const criteriaType =
      type === "originalMarks" ? "criteriaOriginal" : "criteriaUpdated";

    const current = safeCandidates.find(
      (c) =>
        String(c?.centreCandidateNumber) === String(candidateId) ||
        String(c?.candidateNumber) === String(candidateId),
    );

    const saved =
      savedSnapshot?.[candidateId] ?? savedSnapshot?.[String(candidateId)];

    if (!current || !saved) return false;

    const currCriteria = current[criteriaType] || [];
    const savedCriteria = saved[criteriaType] || [];

    if (currCriteria.length !== savedCriteria.length) return true;

    for (let i = 0; i < currCriteria.length; i++) {
      const curr = currCriteria[i];
      const snap = savedCriteria[i];

      if (type === "originalMarks") {
        if (
          curr.assessmentCriteriaName !== snap.assessmentCriteriaName ||
          (curr.originalCentreAssessmentName ?? "") !==
          (snap.originalCentreAssessmentName ?? "") ||
          (curr.originalEvidenceLocation ?? "") !==
          (snap.originalEvidenceLocation ?? "")
        ) {
          return true;
        }
      } else {
        if (
          curr.assessmentCriteriaName !== snap.assessmentCriteriaName ||
          (curr.updatedCentreAssessmentName ?? "") !==
          (snap.updatedCentreAssessmentName ?? "") ||
          (curr.updatedEvidenceLocation ?? "") !==
          (snap.updatedEvidenceLocation ?? "")
        ) {
          return true;
        }
      }
    }

    return false;
  };

  const handleSelectCandidate = (nextCandidateId, type) => {
    setMissingOrInvalidCandidates([]);

    if (
      selectedCandidateId &&
      hasUnsavedChangesFor(selectedCandidateId, type) &&
      !(type === "originalMarks" && originalsSubmitted)
    ) {
      const confirmLeave = window.confirm(
        "You have unsaved changes for this candidate. Do you want to discard them and continue?",
      );

      if (confirmLeave) {
        const snapshot = savedSnapshot[selectedCandidateId];
        if (snapshot) {
          setCandidates((prev) =>
            prev.map((cand) =>
              String(cand.centreCandidateNumber) === String(selectedCandidateId)
                ? {
                  ...cand,
                  criteriaOriginal: JSON.parse(
                    JSON.stringify(snapshot.criteriaOriginal || []),
                  ),
                  criteriaUpdated: JSON.parse(
                    JSON.stringify(snapshot.criteriaUpdated || []),
                  ),
                }
                : cand,
            ),
          );
        }
      } else {
        return;
      }
    }

    setSelectedCandidateId(nextCandidateId);

    const alreadyHasSnapshot = !!savedSnapshot[nextCandidateId];
    if (!alreadyHasSnapshot) {
      const candidate = safeCandidates.find(
        (c) => String(c?.centreCandidateNumber) === String(nextCandidateId),
      );

      if (candidate) {
        setSavedSnapshot((prev) => ({
          ...prev,
          [nextCandidateId]: {
            criteriaOriginal: JSON.parse(
              JSON.stringify(candidate.criteriaOriginal || []),
            ),
            criteriaUpdated: JSON.parse(
              JSON.stringify(candidate.criteriaUpdated || []),
            ),
          },
        }));
      }
    }
  };

  const isUpdatedCandidateRed = (cand, currentCriteriaCount, currentCandidate) => {

    const cid = cand.centreCandidateNumber;

    const yesCount = Number(updatedCriteriaAchievedCount[cid] ?? 0);

    const total =
      updatedTotalCriteriaScore[cid] ??
      Number(cand.criteriaUpdated?.[0]?.updatedTotalCriteriaScore) ??
      0;
    const newTotal = currentCandidate && currentCandidate.centreCandidateNumber === cid ? currentCriteriaCount : total

    const isGreen = yesCount === newTotal

    const allFilled =
      Array.isArray(cand.criteriaUpdated) &&
      cand.criteriaUpdated.every(
        (item) =>
          item.updatedCentreAssessmentName &&
          String(item.updatedCentreAssessmentName).trim() !== "",
      );


    return !(isGreen && allFilled);
  };

  const getAttentionCandidateNames = (currCandidate, newTotal) => {
    if (!safeCandidates.length) return [];

    if (currentSection === "originalMarks") {
      return safeCandidates
        .filter((cand) => {
          const cid = cand.centreCandidateNumber;
          const achieved = criteriaAchievedCount[cid] || 0;
          const total =
            Number(cand.criteriaOriginal?.[0]?.originalTotalCriteriaScore) || 0;

          if (currCandidate && currCandidate.centreCandidateNumber === cid && newTotal !== null) {
            return achieved !== newTotal;
          } else {
            return achieved !== total;
          }

        })
        .map((cand) => cand.candidateName)
        .filter(Boolean);
    }

    return safeCandidates
      .filter((cand) => isUpdatedCandidateRed(cand, newTotal, currCandidate))
      .map((cand) => cand.candidateName)
      .filter(Boolean);
  };

  const ACHIEVED_ID_MAP = {
    [AssessmentCriteriaInputValues.NOT_ATTEMPTED]: 13,
    [AssessmentCriteriaInputValues.YES]: 14,
    [AssessmentCriteriaInputValues.NO]: 15,
  };

  const convertToMarksPayload = (candidatesArr, intent = "save") => {
    const marks = [];

    (candidatesArr || []).forEach((candidate) => {
      const candidateId = Number(
        candidate.candidateNumber ?? candidate.centreCandidateNumber,
      );

      const criteriaList =
        currentSection === "originalMarks"
          ? candidate.criteriaOriginal || []
          : candidate.criteriaUpdated || [];

      criteriaList.forEach((crit) => {
        let achievedVal = mapAssessmentNameToAchieved(
          currentSection === "originalMarks"
            ? crit.originalCentreAssessmentName
            : crit.updatedCentreAssessmentName,
        );

        if (
          achievedVal !== AssessmentCriteriaInputValues.YES &&
          achievedVal !== AssessmentCriteriaInputValues.NO &&
          achievedVal !== AssessmentCriteriaInputValues.NOT_ATTEMPTED
        ) {
          achievedVal = AssessmentCriteriaInputValues.NOT_ATTEMPTED;
        }

        const achievedId = ACHIEVED_ID_MAP[achievedVal];

        const evidence =
          currentSection === "originalMarks"
            ? String(crit.originalEvidenceLocation || "")
            : String(crit.updatedEvidenceLocation || "");
        marks.push({
          centreNumber: Number(candidate.centreNumber) || Number(centerNumber),
          sessionId: Number(sessionId),
          subjectId: Number(subjectId ?? candidate.subjectId),
          assessmentCode: String(candidate.assessmentCode ?? unitCode),
          candidateNumber: candidateId,
          markStatusId:
            intent === "submit" ? MARK_STATUS.RESUBMITTED : MARK_STATUS.SAVED,
          assessmentCriteriaName: crit.assessmentCriteriaName,
          description: crit.assessmentCriteriaDescription || "",
          assessmentCriteriaAchievedId: achievedId,
          evidenceLocation: evidence,
        });
      });
    });

    return {
      marksType: currentSection === "originalMarks" ? "original" : "updated",
      marks,
    };
  };


  const isGreen = (unParsedCandidateId, candidateMap) => {
  // Normalize incoming ID
  const candidateId = String(Number(unParsedCandidateId));

  // Normalize candidateMap keys internally
  const normalizedMap = Object.fromEntries(
    Object.entries(candidateMap || {}).map(([key, value]) => [
      String(Number(key)),
      value,
    ])
  );

  const entry = normalizedMap[candidateId];
  if (!entry) return false;

  const achieved = Number(entry.achievedCount ?? 0);
  const total = Number(entry.candidateMarks ?? 0);

  return achieved === total && total > 0;
};

const isUpdatedGreen = (unParsedCandidateId, updatedCounts, candidates) => {
  // Normalize candidateId
  const candidateId = String(Number(unParsedCandidateId));

  // Normalize updatedCounts keys internally
  const normalizedCounts = Object.fromEntries(
    Object.entries(updatedCounts || {}).map(([key, value]) => [
      String(Number(key)),
      value,
    ])
  );

  const yesCount = Number(normalizedCounts[candidateId] ?? 0);

  const candidate = (Array.isArray(candidates) ? candidates : []).find((c) => {
    const centreId = String(Number(c?.centreCandidateNumber));
    const candId = String(Number(c?.candidateNumber));
    return centreId === candidateId || candId === candidateId;
  });

  if (!candidate) return false;

  const total = Number(
    candidate.criteriaUpdated?.[0]?.updatedTotalCriteriaScore ?? 0
  );

  return yesCount === total && total > 0;
};



  const buildPayloadForDownload = (
    candidatesArr,
    candidateDataMapRef,
    updatedYesCountsRef,
  ) => {
    const buildPayloadForType = (type) => {
      const marks = [];

      (candidatesArr || []).forEach((candidate) => {
        const candidateId = Number(
          candidate.candidateNumber ?? candidate.centreCandidateNumber,
        );

        const criteriaList =
          type === "original"
            ? candidate.criteriaOriginal || []
            : candidate.criteriaUpdated || [];

        criteriaList.forEach((crit) => {
          let achievedVal = mapAssessmentNameToAchieved(
            type === "original"
              ? crit.originalCentreAssessmentName
              : crit.updatedCentreAssessmentName,
          );

          if (
            achievedVal !== AssessmentCriteriaInputValues.YES &&
            achievedVal !== AssessmentCriteriaInputValues.NO &&
            achievedVal !== AssessmentCriteriaInputValues.NOT_ATTEMPTED
          ) {
            achievedVal = AssessmentCriteriaInputValues.NOT_ATTEMPTED;
          }

          const achievedId = ACHIEVED_ID_MAP[achievedVal];

          const evidence =
            type === "original"
              ? String(crit.originalEvidenceLocation || "")
              : String(crit.updatedEvidenceLocation || "");

          const criteriaScore =
            type === "original"
              ? crit.originalTotalCriteriaScore
              : crit.updatedTotalCriteriaScore;

          marks.push({
            centreNumber: Number(centerNumber),
            sessionId: Number(sessionId),
            subjectId: Number(subjectId ?? candidate.subjectId),
            assessmentCode: String(candidate.assessmentCode ?? unitCode),
            candidateNumber: candidateId,
            assessmentCriteriaName: crit.assessmentCriteriaName,
            description: crit.assessmentCriteriaDescription || "",
            assessmentCriteriaAchievedId: achievedId,
            evidenceLocation: evidence,
            criteriaScore: criteriaScore,
            totalMarks: crit.totalMarks,
            isValid:
              type === "original"
                ? isGreen(candidateId, candidateDataMapRef)
                : isUpdatedGreen(
                  candidateId,
                  updatedYesCountsRef,
                  candidatesArr,
                ),
          });
        });
      });

      return {
        marksType: type,
        marks,
      };
    };

    return {
      original: buildPayloadForType("original"),
      updated: buildPayloadForType("updated"),
    };
  };


  // ...existing imports and component code above

  // Helper: persist updated selections per candidate ONLY on Save
  const persistUpdatedSelections = () => {
    try {
      (safeCandidates || []).forEach((candidate) => {
        const cidKey = String(
          candidate.centreCandidateNumber ?? candidate.candidateNumber
        );
        const localKey = `updatedSelections_${centerNumber}_${cidKey}_${subjectId}`;

        // Build { [assessmentCriteriaName]: "yes" | "no" | "not_attempted" }
        const selectionObj = {};
        (candidate.criteriaUpdated || []).forEach((crit) => {
          const achieved = mapAssessmentNameToAchieved(
            crit.updatedCentreAssessmentName
          );
          // Only persist if user actually selected something
          if (
            achieved === AssessmentCriteriaInputValues.YES ||
            achieved === AssessmentCriteriaInputValues.NO ||
            achieved === AssessmentCriteriaInputValues.NOT_ATTEMPTED
          ) {
            selectionObj[crit.assessmentCriteriaName] = achieved;
          }

        });

        localStorage.setItem(localKey, JSON.stringify(selectionObj));
      });
    } catch (e) {
      console.error("Failed to persist updated selections to localStorage", e);
    }
  };

  // Helper: persist page-level state ONLY on Save (so the mount useEffect can restore)
  const persistPageState = () => {
    try {
      const dataToPersist = {
        candidates: safeCandidates,
        savedSnapshot,
        selectedCandidateId,
        showUpdatedSection,
        currentSection,
      };
      localStorage.setItem("acUpdateMarksData", JSON.stringify(dataToPersist));
    } catch (e) {
      console.error("Failed to persist page state to localStorage", e);
    }
  };
  const handleSave = async (rows, saveMode) => {
    try {
      const formattedData = convertToMarksPayload(safeCandidates, "save");
      const response = (currentCentreType === "Lead" && isConsortiaUnit) ? await api.post(
        `saveOrUpdateStage3ACMarksConsortia`,
        formattedData
      ) : await api.post(
        `saveOrUpdateStage3AssessmentCriteriaMarks`,
        formattedData,
      )


      if (response.status === 200) {
        persistUpdatedSelections();
        persistPageState();
        if (saveMode !== "downloadSave") {
          toast.success("Your changes have been saved", {
            style: {
              minWidth: "400px",
            },
          });
        }

        if (selectedCandidateId) {
          const candidate = safeCandidates.find(
            (c) =>
              String(c?.centreCandidateNumber) === String(selectedCandidateId),
          );
          if (candidate) {
            setSavedSnapshot((prev) => ({
              ...prev,
              [selectedCandidateId]: {
                criteriaOriginal: JSON.parse(
                  JSON.stringify(candidate.criteriaOriginal || []),
                ),
                criteriaUpdated: JSON.parse(
                  JSON.stringify(candidate.criteriaUpdated || []),
                ),
              },
            }));
          }
        }

        const attention = getAttentionCandidateNames();
        setMissingOrInvalidCandidates(attention);

        const updatedOriginalMarks =
          updatedTotalCriteriaScore[selectedCandidate.centreCandidateNumber] ??
          selectedCandidate.criteriaUpdated?.[0]?.updatedTotalCriteriaScore ??
          0;
        const updatedCriteriaAcheivedValue =
          updatedCriteriaAchievedCount[
          selectedCandidate.centreCandidateNumber
          ] ?? 0;

        const candidate = safeCandidates.find(
          (c) =>
            String(c.centreCandidateNumber) === String(selectedCandidateId) ||
            String(c.candidateNumber) === String(selectedCandidateId),
        );
const selectedCandidateData = rows.marks.find(
  (c) => parseInt(c.candidateNumber, 10) === parseInt(selectedCandidate.centreCandidateNumber, 10)

);
const centreNumber = selectedCandidateData?.centreNumber;
        if (!candidate) return false;
        if (
          updatedCriteriaAcheivedValue !== updatedOriginalMarks &&
          isUpdatedActiveUI
        ) {
          setUpdateMarksCandidate([
            {
              centreCandidateNumber: selectedCandidate.centreCandidateNumber,
              achievedCount: updatedCriteriaAcheivedValue,
              candidateMarks: updatedOriginalMarks,
              candidateCentreNumber: centreNumber,
            },
          ]);

          setSelectedCandidateId(selectedCandidate.centreCandidateNumber);

          setShowUpdateMarksModal(true);
          setShowUpdatedSection(true);
          setCurrentSection("updatedMarks");

          const attention2 = getAttentionCandidateNames();
          setMissingOrInvalidCandidates(attention2);

          return;
        }
      } else {
        toast.error("Save failed. Please try again.", { duration: 5000 });
      }
    } catch (err) {
      console.error("Save failed", err);
      toast.error("Save failed. Please try again.", { duration: 5000 });
    }
  };

  const isCandidateGreen = (candidateId) => {
    const entry = candidateAchievedCounts?.[candidateId];
    if (!entry) return false;
    const { achievedCount, candidateMarks } = entry;
    if (achievedCount == null || candidateMarks == null) return false;
    return achievedCount === candidateMarks;
  };

  const isUpdatedCandidateGreen = (candidateId) => {
    const yesCount = updatedCriteriaAchievedCount?.[candidateId];
    if (yesCount == null) return false;

    const candidate = safeCandidates.find(
      (c) =>
        String(c.centreCandidateNumber) === String(candidateId) ||
        String(c.candidateNumber) === String(candidateId),
    );
    if (!candidate) return false;
    const total =
      updatedTotalCriteriaScore[candidateId]
    return yesCount === total;
  };

  const areAllOriginalMarksGreen = () => {
    if (!safeCandidates.length) return false;
    return safeCandidates.every((cand) => {
      const filled =
        Array.isArray(cand.criteriaOriginal) &&
        cand.criteriaOriginal.length > 0;
      return (
        filled &&
        isCandidateGreen(cand?.centreCandidateNumber || cand.candidateNumber)
      );
    });
  };

  const areAllUpdatedMarksGreen = () => {
    if (!safeCandidates.length) return false;
    return safeCandidates.every((cand) => {
      const filled =
        Array.isArray(cand.criteriaUpdated) &&
        cand.criteriaUpdated.length > 0 &&
        cand.criteriaUpdated.every(
          (item) =>
            item.updatedCentreAssessmentName &&
            String(item.updatedCentreAssessmentName).trim() !== "",
        );
      return (
        filled &&
        isUpdatedCandidateGreen(
          cand?.centreCandidateNumber || cand.candidateNumber,
        )
      );
    });
  };

  const allCandidatesCompleted = areAllOriginalMarksGreen();
  const allUpdateCandidatesCompleted = areAllUpdatedMarksGreen();

  const isCandidateFullySubmitted = (candidateNumber) => {
    const cand = safeCandidates.find(
      (c) => String(c?.centreCandidateNumber) === String(candidateNumber),
    );
    if (!cand || !Array.isArray(cand.criteriaOriginal)) return false;

    return cand.criteriaOriginal.every((crit) => {
      const achieved = mapAssessmentNameToAchieved(
        crit.originalCentreAssessmentName,
      );
      return achieved === "yes" || achieved === "no";
    });
  };

  const originalsSubmitted = useMemo(() => {
    const safe = Array.isArray(safeCandidates) ? safeCandidates : [];
    if (!safe.length) return false;
    return safe.every((cand) =>
      (cand.criteriaOriginal || []).every(
        (crit) => String(crit.originalMarkStatus) === "Re-submitted",
      ),
    );
  }, [safeCandidates]);

  const updatedSubmitted = useMemo(() => {
    const safe = Array.isArray(safeCandidates) ? safeCandidates : [];
    if (!safe.length) return false;
    return safe.every((cand) =>
      (cand.criteriaUpdated || []).every(
        (crit) => String(crit.updatedMarkStatus) === "Re-submitted",
      ),
    );
  }, [safeCandidates]);

  const areOriginalMarksSubmitted = () => {
    return (
      safeCandidates.length > 0 &&
      safeCandidates.every((c) =>
        isCandidateFullySubmitted(
          c?.centreCandidateNumber || c.candidateNumber,
        ),
      )
    );
  };

  const handleOriginalMarksClick = () => {
    const originals = candidates?.[0]?.criteriaOriginal || [];
    if (originals.some((val) => val.originalMarkStatus === "Re-submitted")) {
      setDisableOriginalMarks(true);
    } else {
      setDisableOriginalMarks(false);
    }
    setShowUpdatedSection(false);
    setCurrentSection("originalMarks");
    setShowOriginalMarksError(false);
    setIsUpdatedActiveUI(false);
  };

  const handleUpdatedMarksClick = () => {
    setIsUpdatedActiveUI(true);
    if (
      candidates?.[0]?.criteriaOriginal?.some(
        (val) => val.originalMarkStatus !== "Re-submitted",
      )
    ) {
      setShowOriginalMarksError(true);
      setShowUpdatedSection(false);
      setCurrentSection("updatedMarks");
      return;
    }
    setShowOriginalMarksError(false);
    setShowUpdatedSection(true);
    setCurrentSection("updatedMarks");
  };


  const handleConfirmSubmit = async () => {

    try {
      const formattedData = convertToMarksPayload(safeCandidates, "submit");

      setIsSystemTransition(true);

      localStorage.removeItem("acUpdateMarksData");
      (safeCandidates || []).forEach((candidate) => {
  const cidKey = String(candidate.centreCandidateNumber ?? candidate.candidateNumber);
  const localKey = `updatedSelections_${centerNumber}_${cidKey}_${subjectId}`;
  localStorage.removeItem(localKey);
});

      let endpoint = ''
      if (isConsortiaUnit === undefined || isConsortiaUnit === null) { return; }
      if (['Lead', 'Rest'].includes(currentCentreType) && isConsortiaUnit) {
        endpoint = 'submitStage3ACMarksConsortia'
      } else {
        endpoint = "submitStage3AssessmentCriteriaMarks"
      }
      const response = await api.post(
        endpoint,
        formattedData,
      );


      if (response.status !== 200) {
        throw new Error("Submit failed");
      }

      if (formattedData.marksType === "original") {
        toast.success(
          "Your assessment criteria original marks have been submitted",
          { style: { minWidth: "500px" } },
        );

        const freshCandidates = await fetchCandidates();

        const first = freshCandidates?.[0];
        if (first) {
          setSelectedCandidateId(first.centreCandidateNumber);

          setSavedSnapshot({
            [first.centreCandidateNumber]: {
              criteriaOriginal: JSON.parse(
                JSON.stringify(first.criteriaOriginal || []),
              ),
              criteriaUpdated: JSON.parse(
                JSON.stringify(first.criteriaUpdated || []),
              ),
            },
          });
        }

        setShowOriginalMarksError(false);
        setShowUpdatedSection(true);
        setCurrentSection("updatedMarks");
        setIsUpdatedActiveUI(true);
        setIsSubmitModalOpen(false);
      }

      if (formattedData.marksType === "updated") {
        toast.success("Your assessment criteria marks have been submitted", {
          style: { minWidth: "500px" },
        });

        setIsSubmitModalOpen(false);

        navigate(`/dashboard/marks_submission/${subjectId}`, {
          state: { mode: "criteria" },
        });
      }
    } catch (err) {
      console.error("Submit failed", err);
      closeSubmitModal();
    } finally {
      setIsSystemTransition(false);
    }
  };

  const submissionPayload = useMemo(
    () => convertToMarksPayload(safeCandidates, "save"),
    [safeCandidates],
  );
  const candidateDataMap = candidateAchievedCounts;
  const downloadPayload = useMemo(
    () =>
      buildPayloadForDownload(
        safeCandidates,
        candidateAchievedCounts,
        updatedCriteriaAchievedCount,
      ),
    [safeCandidates, candidateAchievedCounts, updatedCriteriaAchievedCount],
  );

  const SuccessIcon = () => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <circle
        cx="12"
        cy="12"
        r="10"
        fill="#FFFFFF"
        stroke="#2B8000"
        strokeWidth="2"
      />
      <path
        d="M8 12.5l2.5 2.5L16 9.5"
        fill="none"
        stroke="#2B8000"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  let totalCriteria =
    currentSection === "originalMarks" && selectedCandidate?.criteriaOriginal
      ? selectedCandidate?.criteriaOriginal.length
      : currentSection === "updatedMarks" && selectedCandidate?.criteriaUpdated
        ? selectedCandidate?.criteriaUpdated.length
        : 0;
  return (
    <div className="ac-mark-dashboard pnl_page_layout columns is-gapless">
      <div className="pnl_page_content column">
        <Toaster
          position="top-right"
          containerStyle={{ marginTop: "8rem" }}
          toastOptions={{
            style: {
              width: "350px",
              borderRadius: "4px",
              fontFamily:
                "'Open Sans', 'Source Sans 3', Arial, Helvetica, sans-serif",
              boxShadow:
                "rgba(0, 0, 0, 0.14) 0px 8px 12px -4px, rgba(0, 0, 0, 0.24) 0px 4px 6px -2px",
            },
            success: {
              style: {
                backgroundColor: "#f5faf5",
                border: "1px solid #2B8000",
                color: "#2B8000",
              },
              icon: <SuccessIcon />,
            },
          }}
        />

        <ACUpdateMarksSidePanel
          currentSection={currentSection}
          assessmentCandidateData={safeCandidates}
          candidateDataMap={candidateDataMap}
          selectedCandidate={selectedCandidateId}
          mode={mode}
          onOriginalMarksClick={handleOriginalMarksClick}
          onUpdatedMarksClick={handleUpdatedMarksClick}
          setSelectedCandidate={handleSelectCandidate}
          updatedTotalCriteriaScore={updatedTotalCriteriaScore}
          updatedCriteriaAchievedCount={updatedCriteriaAchievedCount}
          safeCandidates={safeCandidates}
        />

        <UpdateACMarksConfirmModal
          isOpen={showUpdateMarksModal}
          candidates={updateMarksCandidate}
          onConfirm={async () => {
            await fetchCandidates();
            const candidate = updateMarksCandidate[0];
            if (!candidate) return;

            const newTotal = candidate.achievedCount;


            const attention = getAttentionCandidateNames(candidate, newTotal);

            setMissingOrInvalidCandidates(attention);

            setShowUpdateMarksModal(false);
          }}
          onCancel={() => {
            setShowUpdateMarksModal(false);
          }}
        />

        <ACUpdateMarksCentrePanel
          originalsSubmitted={originalsSubmitted}
          updatedSubmitted={updatedSubmitted}
          onSubmit={isViewMode ? undefined : openSubmitModal}
          onSave={isViewMode ? undefined : handleSave}
          candidatePayloadData={safeCandidates}
          selectedCandidateObj={selectedCandidate}
          criteriaAchievedCount={criteriaAchievedCount}
          candidateMarks={candidateMarks}
          showOriginalMarksError={showOriginalMarksError}
          isUpdatedActive={isUpdatedActiveUI}
          totalMarks={totalMarks}
          originalTotalCriteriaScore={originalTotalCriteriaScore}
          submissionPayload={submissionPayload}
          downloadPayload={downloadPayload}
          selectedCandidate={selectedCandidateId}
          candidateDataMap={candidateDataMap}
          updatedTotalCriteriaScores={updatedTotalCriteriaScore}
          allCandidatesCompleted={
            currentSection === "originalMarks"
              ? allCandidatesCompleted
              : allUpdateCandidatesCompleted
          }
          mode={mode}
          currentSection={currentSection}
          updatedCriteriaAchievedCount={updatedCriteriaAchievedCount}
          showModal={showModal}
          setShowModal={setShowModal}
          handleConfirmSubmit={handleConfirmSubmit}
          totalCriteria={totalCriteria}
          // pass attention candidates to centre panel for banner + header
          missingOrInvalidCandidates={missingOrInvalidCandidates}
        />

        <UpdateConfirmSubmitModal
          isOpen={isSubmitModalOpen}
          onConfirm={handleConfirmSubmit}
          onCancel={closeSubmitModal}
        />

        {!showUpdatedSection &&
          !showOriginalMarksError &&
          selectedCandidate && (
            <AssessmentCriteriaOriginalTable
              criteriaDetails={selectedCandidate.criteriaOriginal || []}
              values={originalValues}
              candidateId={selectedCandidate.centreCandidateNumber}
              onChangeNote={onChangeNote}
              notes={originalNotes}
              onChangeValue={onChangeValue}
              onCriteriaAchievedChange={(cid, count) =>
                setCriteriaAchievedCount((prev) => ({
                  ...prev,
                  [cid]: count,
                }))
              }
              mode={isViewMode ? "view" : mode}
              disableOriginalMarks={disableOriginalMarks}
            />
          )}

        {showUpdatedSection && !showOriginalMarksError && selectedCandidate && (
          <AssessmentCriteriaUpdatedTable
            criteriaDetails={selectedCandidate?.criteriaUpdated || []}
            values={updatedValues}
            candidateId={selectedCandidate?.centreCandidateNumber}
            onChangeNote={onChangeUpdatedNote}
            notes={updatedNotes}
            onChangeValue={onChangeUpdatedValue}
            // FIX: updated table should update updatedCriteriaAchievedCount
            onCriteriaAchievedChange={(cid, count) =>
              setUpdatedCriteriaAchievedCount((prev) => ({
                ...prev,
                [cid]: count,
              }))
            }
            mode={isViewMode ? "view" : mode}
          />
        )}
      </div>
    </div>
  );
};

export default ACUpdateMarksDashboardPage;